import { ApolloLink } from '@apollo/client';
import * as Sentry from '@sentry/react';

import {
  EnergyBasketFragment,
  EnergyFuelType,
  EnergyQuoteRequirementsInput,
  MutationSelectEnergyQuotesArgs,
  MutationUpdateBasketPreferencesArgs,
  MutationUpdateEnergyQuoteRequirementsArgs,
  SelectEnergyQuotesMutation,
  SetEnergyDetailsMutation,
  UpdateBasketPreferencesMutation,
} from '@generated/gql/graphql.ts';

type SubmitEnergyParams = {
  variables: MutationUpdateEnergyQuoteRequirementsArgs;
};

type UpdateBasketPreferences = {
  variables: MutationUpdateBasketPreferencesArgs;
};

type SelectQuoteParams = {
  variables: MutationSelectEnergyQuotesArgs;
};

interface HandleEnergyFormInputParams {
  energyDetails: EnergyQuoteRequirementsInput;
  fuelType: EnergyFuelType;
  energy: EnergyBasketFragment;
  updateRequirements: (
    params: SubmitEnergyParams
  ) => Promise<ApolloLink.Result<SetEnergyDetailsMutation>>;
  updatePreferences: (
    params: UpdateBasketPreferences
  ) => Promise<ApolloLink.Result<UpdateBasketPreferencesMutation>>;
  selectQuote: (
    params: SelectQuoteParams
  ) => Promise<ApolloLink.Result<SelectEnergyQuotesMutation>>;
}

export const handleEnergyFormInput = async ({
  energyDetails,
  fuelType,
  energy,
  updateRequirements,
  updatePreferences,
  selectQuote,
}: HandleEnergyFormInputParams) => {
  const { basketId } = energyDetails;

  if (!energyDetails.gas && !energyDetails.electricity) {
    return;
  }

  if (energy.fuelType !== fuelType) {
    await updatePreferences({
      variables: {
        basketId,
        input: {
          energyFuelType: fuelType,
        },
      },
    });
  }

  const updateResult = await updateRequirements({
    variables: { details: energyDetails },
  });

  if (!energy?.selectedQuote?.isSelected) {
    return updateResult.data?.updateEnergyQuoteRequirements;
  }

  const { selectedQuote } = energy;

  if (!selectedQuote.quoteType) {
    // Should never happen
    Sentry.captureMessage(
      'Update energy quote requirements: Selected quoteType is missing'
    );
    return;
  }

  const selectResult = await selectQuote({
    variables: {
      input: {
        basketId,
        quoteType: selectedQuote.quoteType,
      },
    },
  });

  return selectResult.data?.selectEnergyQuotes;
};
